import { useDispatch } from 'react-redux';
import { useState } from 'react';
import { useProductSelection } from '../../shared/product-selection/useProductSelection';
import { AgeCategory } from '../../../models/AgeCategory';
import {
    isExcludeProductSelection,
    isIncludeProductSelection,
    ProductSelection
} from '../../shared/product-selection/ProductSelectionTypes';
import { PriceTier, serializePriceTier } from '../../../models/PriceTier';
import { SubscriptionPeriodType } from '../../../models/Article';
import {
    addBulkFromCatalogueByIds,
    AddBulkFromCatalogueByIdsRequest
} from '../../api/selection-list/addBulkFromCatalogueByIds';
import {
    addBulkFromCatalogueByFilter,
    AddBulkFromCatalogueByFilterRequest
} from '../../api/selection-list/addBulkFromCatalogueByFilter';
import { BulkAddProductsToSelectionListResponse } from '../../store/api';
import { deserializeFilter } from '../../shared/utils/deserializeFilter';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { SOURCE_CATALOGUE } from '../../store/constants';
import { LoanFormatState } from '../../store/loanFormat/state';
import { mapLoanFormatStateToDto } from '../../../models/LoanFormatDto';
import { bulkUpdateToList } from '../../api/selection-list/bulkUpdateToList';

type GetRequestParams = {
    selectionListId: string;
    ageCategory: AgeCategory;
    productSelection: ProductSelection;
    filterRequest: string;
    loanFormat: LoanFormatState;
    priceTier?: PriceTier;
    performChecks: boolean;
    shopLanguage: string | undefined;
};

export const buildAddProductsCallback = (
    {
        selectionListId,
        ageCategory,
        productSelection,
        filterRequest,
        loanFormat,
        priceTier,
        performChecks,
        shopLanguage
    }: GetRequestParams,
    t: TFunction
): (() => Promise<BulkAddProductsToSelectionListResponse>) => {
    return () => {
        if (isIncludeProductSelection(productSelection)) {
            const body: AddBulkFromCatalogueByIdsRequest = {
                productIds: productSelection.included.map(({ productId }) => productId),
                targetPrice: serializePriceTier(priceTier)!,
                subscriptionPeriodType: SubscriptionPeriodType.CURRENT
            };

            return addBulkFromCatalogueByIds({
                selectionListId,
                body,
                params: { performChecks }
            });
        } else if (isExcludeProductSelection(productSelection)) {
            const body: AddBulkFromCatalogueByFilterRequest = {
                filterRequest: deserializeFilter(filterRequest, {
                    includeKey: 'include',
                    excludeKey: 'exclude'
                }),
                excludedProductIds: productSelection.excluded.map(({ productId }) => productId),
                targetPrice: serializePriceTier(priceTier)!,
                subscriptionPeriodType: SubscriptionPeriodType.CURRENT,
                loanFormat: mapLoanFormatStateToDto(loanFormat),
                ageCategory: ageCategory !== AgeCategory.ALL_AGES ? ageCategory : undefined,
                shopLanguage
            };

            return addBulkFromCatalogueByFilter({ selectionListId, body, params: { performChecks } });
        } else {
            throw new Error(t('notifications.buildAddProductsCallback.error'));
        }
    };
};

type Props = {
    ageCategory: AgeCategory;
    loanFormat: LoanFormatState;
    filter: string;
    shopLanguage: string | undefined;
};

export const useAddBulkToSelectionlist = ({ ageCategory, filter, loanFormat, shopLanguage }: Props) => {
    const { t } = useTranslation();
    const dispatchRedux = useDispatch();
    const [isLoading, setLoading] = useState(false);

    const { productSelection: productSelectionContext } = useProductSelection();

    return {
        isLoading,
        addToListBulk: (
            listId: string,
            priceTier: PriceTier,
            performChecks: boolean,
            productSelection?: ProductSelection
        ) => {
            const apiCallback = buildAddProductsCallback(
                {
                    selectionListId: listId,
                    ageCategory,
                    productSelection: productSelection ?? productSelectionContext,
                    filterRequest: filter,
                    loanFormat,
                    priceTier,
                    performChecks,
                    shopLanguage
                },
                t
            );

            return bulkUpdateToList({
                performChecks,
                dispatch: dispatchRedux,
                setLoading,
                source: SOURCE_CATALOGUE,
                apiCallback,
                t
            });
        }
    };
};
